import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Divider,
  Stack,
  Button,
  Container,
  Card,
  CardContent,
  Fade,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getUserByID } from 'services/users/getUserByID';
import { useNavigate } from 'react-router-dom';
import { Edit, Email, Phone, Cake, Store, CardMembership } from '@mui/icons-material';

const ViewProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const roleId = localStorage.getItem('roleid');
  const navigate = useNavigate();
  const [openImageDialog, setOpenImageDialog] = React.useState(false);

  const { data: userData, isLoading } = useQuery({
    queryKey: ['getUserByID', roleId],
    queryFn: () => getUserByID({ roleid: Number(roleId) }),
    enabled: !!roleId
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading || !userData) {
    return (
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Loading Profile...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Fade in timeout={600}>
        <Card
          sx={{
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[100]} 100%)`
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'center' : 'flex-start',
                mb: 4,
                gap: 2
              }}
            >
              <Stack
                direction={isMobile ? 'column' : 'row'}
                alignItems="center"
                spacing={2}
                sx={{ textAlign: isMobile ? 'center' : 'left' }}
              >
                <Avatar
                  src={userData.avatar}
                  alt={`${userData.firstname} ${userData.lastname}`}
                  sx={{
                    width: { xs: 80, sm: 120 },
                    height: { xs: 80, sm: 120 },
                    border: `3px solid ${theme.palette.primary.main}`,
                    cursor: 'pointer' // Add pointer cursor
                  }}
                  onClick={() => setOpenImageDialog(true)} // Open the dialog
                />

                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {`${userData.firstname} ${userData.lastname}`}
                  </Typography>
                  <Chip
                    label={roleId === '2' ? 'Student' : 'Coach'}
                    color={roleId === '2' ? 'success' : 'info'}
                    size="small"
                    sx={{ mt: 1, fontWeight: 500 }}
                  />
                </Box>
              </Stack>
              <Button variant="contained" color="primary" startIcon={<Edit />} onClick={() => navigate('/profile/editprofile')}>
                Edit Profile
              </Button>
            </Box>

            <Divider sx={{ mb: 4, borderColor: theme.palette.grey[300] }} />

            {/* Info Grid */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Email color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={500} color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{userData.emailaddress || 'N/A'}</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Phone color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={500} color="text.secondary">
                      Mobile
                    </Typography>
                    <Typography variant="body1">{userData.mobilenumber || 'N/A'}</Typography>
                  </Box>
                </Stack>
              </Grid>

              {roleId === '2' && (
                <Grid item xs={12} sm={6} md={4}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Cake color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight={500} color="text.secondary">
                        Date of Birth
                      </Typography>
                      <Typography variant="body1">{formatDate(userData.dateOfBirth)}</Typography>
                    </Box>
                  </Stack>
                </Grid>
              )}

              {roleId === '3' && (
                <>
                  <Grid item xs={12} sm={6} md={4}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Store color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary">
                          Studio Name
                        </Typography>
                        <Typography variant="body1">{userData.studioName || 'N/A'}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CardMembership color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary">
                          Subscription Plan
                        </Typography>
                        <Typography variant="body1">{userData.subscriptionPlan || 'N/A'}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="md" fullWidth>
        <Box sx={{ position: 'relative', backgroundColor: 'black' }}>
          <img src={userData.avatar} alt="Profile" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
          <Button
            onClick={() => setOpenImageDialog(false)}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </Container>
  );
};

export default ViewProfile;
